import i18n from "i18next";
import { initReactI18next } from "react-i18next";

const getInitialLang = () => {
  if (typeof window === "undefined") return "en";
  const params = new URLSearchParams(window.location.search);
  const fromQuery = params.get("lng");
  if (fromQuery === "he" || fromQuery === "en") return fromQuery;
  return localStorage.getItem("lang") || "en";
};

const resources = {
  en: {
    translation: {
      welcome: "Welcome",
      home_subtitle: "Let's see how good you are in blackjack",
      trainer: "Trainer",
      strategy_calculator: "Strategy Calculator",
      accuracy: "Accuracy",
      deal_hand: "Deal Hand",
      next_hand: "Next Hand",
      your_hand: "Your Hand",
      dealer: "Dealer",
      correct: "✓ Correct!",
      incorrect: "✗ Incorrect",
      optimal_move: "Optimal move was:",
      loading_strategy: "Loading strategy...",
      your_cards: "Your Cards",
      first_card: "First Card",
      second_card: "Second Card",
      select_card: "Select card",
      dealers_up_card: "Dealer's Up Card",
      select_dealer_card: "Select dealer card",
      pair: "Pair",
      optimal_move_title: "Optimal Move",
      // Action labels
      Hit: "Hit",
      Stand: "Stand",
      Double: "Double",
      Surrender: "Surrender",
      "Double/Stand": "Double/Stand",
      Split: "Split",
      // Hints/explanations
      hint_hit_safe: "You can't bust, so hitting is always safe!",
      hint_hit_strong_dealer:
        "Dealer has a strong card, so you need to try to improve your hand.",
      hint_hit_weak:
        "You have a weak hand, so hitting gives you a better chance to win.",
      hint_stand_high:
        "Standing on 17 or more is usually best, as hitting risks busting.",
      hint_stand_dealer_bust: "Dealer is likely to bust, so standing is safer.",
      hint_stand_decent:
        "You have a decent hand, and the dealer is in a weak position.",
      hint_double:
        "Doubling lets you win more when you have a strong advantage!",
      hint_split: "Splitting gives you two chances to win with a strong pair.",
      hint_surrender: "Surrendering loses less money in tough situations.",
      hint_default: "This is the mathematically best move for this situation.",
      card_counting_trainer: "Card Counting Trainer",
      card_counting_nav: "Card Counting",
      card_counting_placeholder: "Count",
      card_counting_check: "Check",
      card_counting_deal: "Deal New Cards",
      card_counting_correct: "Correct!",
      card_counting_incorrect: "Incorrect. The correct count is {{count}}",
      card_counting_answer: "The correct count is",
      restart_score: "Restart Score",
      // SEO / content
      seo_home_h1: "Free Blackjack Trainer & Strategy Chart",
      seo_home_intro:
        "Practice perfect basic strategy, look up any hand in the calculator, and drill Hi-Lo card counting — all free, no signup.",
      seo_home_section_title: "Learn Blackjack the smart way",
      seo_home_section_body:
        "HitOrStand is a free Blackjack training app for anyone who wants to stop guessing at the table. The trainer deals real 6-deck shoe hands and tells you instantly whether your decision — hit, stand, double, split, or surrender — matches optimal basic strategy. A few minutes a day is enough to commit the chart to memory.",
      seo_home_features_title: "What you get",
      seo_home_feature_trainer:
        "Blackjack Trainer: practice every hand type (hard totals, soft hands, pairs) with instant feedback and a running accuracy score.",
      seo_home_feature_calc:
        "Strategy Calculator: punch in any two player cards and the dealer's up card to see the mathematically optimal move.",
      seo_home_feature_counting:
        "Card Counting Trainer: drill the Hi-Lo running count at your own pace until counting a shoe becomes effortless.",
      seo_home_why_title: "Why basic strategy matters",
      seo_home_why_body:
        "Blackjack is the casino game with the lowest house edge — but only if you play every hand correctly. Perfect basic strategy cuts the casino's edge to roughly 0.5% in a standard 6-deck shoe. Every deviation costs money. The trainer is built to make those deviations stop happening.",
      faq_title: "Frequently Asked Questions",
      faq_q1: "What is the best way to learn Blackjack basic strategy?",
      faq_a1:
        "Practice every hand against a strategy chart until the correct move is automatic. HitOrStand deals real Blackjack hands and gives instant feedback so you build muscle memory for hit, stand, double, split, and surrender decisions in minutes a day.",
      faq_q2: "Is HitOrStand free to use?",
      faq_a2:
        "Yes. The Blackjack trainer, strategy calculator, and card counting trainer are all completely free and require no signup. It works in any modern browser on desktop or mobile.",
      faq_q3: "Does basic strategy really work at the Blackjack table?",
      faq_a3:
        "Yes. Basic strategy is mathematically derived to minimize the house edge. Playing perfectly reduces the casino's advantage to about 0.5% in standard 6-deck shoe games, which is the lowest edge of any common casino game.",
      faq_q4: "How does card counting work?",
      faq_a4:
        "The Hi-Lo system assigns +1 to low cards (2-6), 0 to neutral cards (7-9), and -1 to high cards (10-A). A higher running count means more high cards remain, which favors the player. HitOrStand's counting trainer deals random cards and checks your running count.",
      faq_q5: "When should I hit or stand in Blackjack?",
      faq_a5:
        "A common rule of thumb: stand on hard 17 or higher, hit hard 8 or lower, and on totals 12-16 stand against dealer 2-6 (bust cards) and hit against 7-A. For full accuracy, use the HitOrStand strategy calculator with your exact hand.",
      // Nav extras
      nav_chart: "Strategy Chart",
      nav_glossary: "Glossary",
      nav_about: "About",
      nav_how_to: "How to Use",
      // Tabbar short labels — fit in one line
      tab_trainer: "Trainer",
      tab_calc: "Calc",
      tab_count: "Count",
      tab_chart: "Chart",
    },
  },
  he: {
    translation: {
      welcome: "ברוכים הבאים",
      home_subtitle: "בואו נראה כמה אתם טובים בבלקג׳ק",
      trainer: "מאמן",
      strategy_calculator: "מחשבון אסטרטגיה",
      accuracy: "דיוק",
      deal_hand: "חלק קלפים",
      next_hand: "יד הבאה",
      your_hand: "היד שלך",
      dealer: "דילר",
      correct: "✓ נכון!",
      incorrect: "✗ לא נכון",
      optimal_move: "המהלך האופטימלי היה:",
      loading_strategy: "טוען אסטרטגיה...",
      your_cards: "הקלפים שלך",
      first_card: "קלף ראשון",
      second_card: "קלף שני",
      select_card: "בחר קלף",
      dealers_up_card: "הקלף הגלוי של הדילר",
      select_dealer_card: "בחר קלף לדילר",
      pair: "זוג",
      optimal_move_title: "המהלך האופטימלי",
      // Action labels
      Hit: "לקחת קלף",
      Stand: "להישאר",
      Double: "להכפיל",
      Surrender: "להיכנע",
      "Double/Stand": "להכפיל/להישאר",
      Split: "לפצל",
      // Hints/explanations
      hint_hit_safe: "אתה לא יכול להפסיד, אז תמיד כדאי לקחת קלף!",
      hint_hit_strong_dealer: "לדילר יש קלף חזק, כדאי לנסות לשפר את היד שלך.",
      hint_hit_weak: "היד שלך חלשה, לקיחת קלף תיתן לך סיכוי טוב יותר לנצח.",
      hint_stand_high:
        "להישאר על 17 ומעלה זה בדרך כלל הכי טוב, כי לקיחת קלף מסכנת אותך.",
      hint_stand_dealer_bust: "לדילר יש סיכוי להפסיד, אז להישאר זה בטוח יותר.",
      hint_stand_decent: "יש לך יד טובה, והדילר במצב חלש.",
      hint_double: "הכפלה נותנת לך אפשרות להרוויח יותר כשיש לך יתרון חזק!",
      hint_split: "פיצול נותן לך שתי הזדמנויות לנצח עם זוג חזק.",
      hint_surrender: "כניעה מפסידה פחות כסף במצבים קשים.",
      hint_default: "זהו המהלך המתמטי הטוב ביותר למצב הזה.",
      card_counting_trainer: "מאמן ספירת קלפים",
      card_counting_nav: "ספירת קלפים",
      card_counting_placeholder: "ספירה",
      card_counting_check: "בדוק",
      card_counting_deal: "חלק קלפים חדשים",
      card_counting_correct: "נכון!",
      card_counting_incorrect: "לא נכון. הספירה הנכונה היא {{count}}",
      card_counting_answer: "הספירה הנכונה היא",
      restart_score: "אפס ניקוד",
      // SEO / content
      seo_home_h1: "מאמן בלקג׳ק חינמי וטבלת אסטרטגיה",
      seo_home_intro:
        "תרגלו אסטרטגיה בסיסית מושלמת, בדקו כל יד במחשבון, ואמנו ספירת קלפים בשיטת Hi-Lo — הכל בחינם, ללא הרשמה.",
      seo_home_section_title: "ללמוד בלקג׳ק בדרך החכמה",
      seo_home_section_body:
        "HitOrStand הוא אפליקציית אימון בלקג׳ק חינמית לכל מי שרוצה להפסיק לנחש ליד השולחן. המאמן מחלק ידיים אמיתיות משו של 6 חפיסות ואומר לכם מיד אם הבחירה — לקיחת קלף, להישאר, להכפיל, לפצל או להיכנע — תואמת לאסטרטגיה הבסיסית האופטימלית. מספר דקות ביום מספיקות כדי לשנן את הטבלה.",
      seo_home_features_title: "מה תקבלו",
      seo_home_feature_trainer:
        "מאמן בלקג׳ק: תרגלו כל סוג של יד (סכומים קשים, ידיים רכות, זוגות) עם משוב מיידי וניקוד דיוק שוטף.",
      seo_home_feature_calc:
        "מחשבון אסטרטגיה: הכניסו שני קלפים והקלף הגלוי של הדילר כדי לראות את המהלך המתמטי האופטימלי.",
      seo_home_feature_counting:
        "מאמן ספירת קלפים: תרגלו את ספירת Hi-Lo בקצב שלכם עד שספירת שו הופכת לטבעית.",
      seo_home_why_title: "למה אסטרטגיה בסיסית חשובה",
      seo_home_why_body:
        "בלקג׳ק הוא משחק הקזינו עם יתרון הבית הנמוך ביותר — אבל רק אם משחקים כל יד נכון. אסטרטגיה בסיסית מושלמת מורידה את יתרון הקזינו ל-0.5% בערך בשו סטנדרטי של 6 חפיסות. כל סטייה עולה כסף. המאמן בנוי בדיוק כדי שהסטיות האלה יפסיקו לקרות.",
      faq_title: "שאלות נפוצות",
      faq_q1: "מה הדרך הטובה ביותר ללמוד אסטרטגיה בסיסית בבלקג׳ק?",
      faq_a1:
        "תרגלו כל יד מול טבלת אסטרטגיה עד שהמהלך הנכון הופך לאוטומטי. HitOrStand מחלק ידיים אמיתיות ונותן משוב מיידי כך שתבנו זיכרון שריר להחלטות לקיחת קלף, להישאר, להכפיל, לפצל וכניעה תוך דקות ביום.",
      faq_q2: "האם השימוש ב-HitOrStand חינמי?",
      faq_a2:
        "כן. מאמן הבלקג׳ק, מחשבון האסטרטגיה ומאמן ספירת הקלפים — כולם חינמיים לחלוטין ולא דורשים הרשמה. עובד בכל דפדפן מודרני במחשב או בנייד.",
      faq_q3: "האם אסטרטגיה בסיסית באמת עובדת בשולחן הבלקג׳ק?",
      faq_a3:
        "כן. אסטרטגיה בסיסית מבוססת על חישוב מתמטי כדי למזער את יתרון הבית. משחק מושלם מוריד את יתרון הקזינו ל-0.5% בערך בשו סטנדרטי של 6 חפיסות — היתרון הנמוך ביותר בכל משחק קזינו נפוץ.",
      faq_q4: "איך עובדת ספירת קלפים?",
      faq_a4:
        "שיטת Hi-Lo מקצה +1 לקלפים נמוכים (2-6), 0 לקלפים נייטרליים (7-9), ו--1 לקלפים גבוהים (10-A). ספירה גבוהה יותר אומרת שיותר קלפים גבוהים נשארו בשו, מה שמעדיף את השחקן. מאמן הספירה של HitOrStand מחלק קלפים אקראיים ובודק את הספירה שלכם.",
      faq_q5: "מתי כדאי לקחת קלף ומתי להישאר בבלקג׳ק?",
      faq_a5:
        "כלל אצבע שימושי: הישארו על 17 קשה ומעלה, קחו קלף על 8 קשה ומטה, ובסכומים 12-16 הישארו מול 2-6 של הדילר (קלפי שריפה) וקחו קלף מול 7-A. לדיוק מלא, השתמשו במחשבון האסטרטגיה של HitOrStand עם היד המדויקת שלכם.",
      // Nav extras
      nav_chart: "טבלת אסטרטגיה",
      nav_glossary: "מילון מונחים",
      nav_about: "אודות",
      nav_how_to: "איך משתמשים",
      // Tabbar short labels
      tab_trainer: "מאמן",
      tab_calc: "מחשבון",
      tab_count: "ספירה",
      tab_chart: "טבלה",
    },
  },
};

i18n.use(initReactI18next).init({
  resources,
  lng: getInitialLang(),
  fallbackLng: "en",
  interpolation: {
    escapeValue: false,
  },
});

// Save language on change
i18n.on("languageChanged", (lng) => {
  localStorage.setItem("lang", lng);
  if (typeof document !== "undefined") {
    document.documentElement.lang = lng;
    document.documentElement.dir = lng === "he" ? "rtl" : "ltr";
  }
});

export default i18n;
